/* Step 5 — Success. Shows downloads + lazily-polls for the AI 3D elevation. */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { CheckCircle2, Download, FileText, ImageIcon, Sparkles, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function StepSuccess({ result }) {
  const [status, setStatus] = useState(null);

  // Poll the request status until the 3D elevation lands (or we give up).
  useEffect(() => {
    let cancel = false;
    let tries = 0;
    const tick = async () => {
      if (cancel) return;
      tries += 1;
      try {
        const { data } = await axios.get(`${API}/studio/requests/${result.request_id}/status`);
        if (cancel) return;
        setStatus(data);
        if (data.ai_elevation_url || tries >= 60) return;     // ~5 min cap
      } catch {
        if (tries >= 60) return;
      }
      setTimeout(tick, 5000);
    };
    tick();
    return () => { cancel = true; };
  }, [result.request_id]);

  const elevationUrl = status?.ai_elevation_url
    ? `${process.env.REACT_APP_BACKEND_URL}${status.ai_elevation_url}`
    : null;

  const downloads = [
    { id: "pdf", label: "Floor Plan (PDF)", icon: <FileText className="h-5 w-5" />, url: `${API}/studio/requests/${result.request_id}/floor-plan.pdf` },
    { id: "png", label: "Floor Plan (PNG)", icon: <ImageIcon className="h-5 w-5" />, url: `${API}/studio/requests/${result.request_id}/floor-plan.png` },
    { id: "svg", label: "Floor Plan (SVG)", icon: <ImageIcon className="h-5 w-5" />, url: `${API}/studio/requests/${result.request_id}/floor-plan.svg` },
  ];

  return (
    <section data-testid="studio-step-success">
      <div className="rounded-3xl bg-emerald-50 border-2 border-emerald-200 p-5 sm:p-6">
        <div className="flex items-start gap-3">
          <CheckCircle2 className="h-8 w-8 text-emerald-600 flex-shrink-0" />
          <div className="flex-1">
            <div className="text-[11px] uppercase tracking-[0.2em] text-emerald-700 font-bold">Payment successful</div>
            <h2 className="mt-1 text-2xl sm:text-3xl font-extrabold text-navy">Your designs are ready!</h2>
            <p className="mt-1 text-sm text-emerald-900">
              Receipt: <code className="font-mono font-bold" data-testid="studio-success-receipt">{result.receipt_no}</code>
            </p>
          </div>
        </div>
      </div>

      {/* Downloads */}
      <div className="mt-5 rounded-3xl bg-white border-2 border-border p-5">
        <div className="text-[11px] uppercase tracking-[0.2em] text-gold-dark font-bold">Downloads</div>
        <h3 className="mt-1 text-lg font-extrabold text-navy">2D Floor Plan</h3>
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-3">
          {downloads.map((d) => (
            <a
              key={d.id}
              href={d.url}
              target="_blank"
              rel="noreferrer"
              data-testid={`studio-download-${d.id}`}
              className="rounded-2xl bg-navy text-white px-4 py-4 hover:bg-navy-600 transition-colors flex items-center justify-between gap-2"
            >
              <span className="flex items-center gap-2 text-sm font-bold">
                {d.icon}
                {d.label}
              </span>
              <Download className="h-4 w-4 text-gold" />
            </a>
          ))}
        </div>
      </div>

      {/* 3D Elevation */}
      <div className="mt-5 rounded-3xl bg-white border-2 border-border p-5">
        <div className="flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-gold-dark" />
          <div className="text-[11px] uppercase tracking-[0.2em] text-gold-dark font-bold">AI Generated</div>
        </div>
        <h3 className="mt-1 text-lg font-extrabold text-navy">3D Elevation</h3>
        <p className="text-[12.5px] text-muted-foreground">Modern style · front three-quarter view</p>

        <div className="mt-3 aspect-[16/10] rounded-2xl bg-sand-alt border-2 border-border overflow-hidden relative" data-testid="studio-elevation-container">
          {elevationUrl ? (
            <img
              src={elevationUrl}
              alt="AI generated 3D elevation"
              className="absolute inset-0 w-full h-full object-cover"
              data-testid="studio-elevation-image"
            />
          ) : (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-center px-4">
              <Loader2 className="h-7 w-7 text-navy animate-spin" />
              <div className="text-sm font-bold text-navy">Generating your 3D elevation…</div>
              <div className="text-[11.5px] text-muted-foreground max-w-md">
                Nano Banana is rendering a photoreal front view of your home.
                This usually takes 20-40 seconds.
              </div>
            </div>
          )}
        </div>

        {elevationUrl && (
          <div className="mt-3">
            <a
              href={elevationUrl}
              target="_blank"
              rel="noreferrer"
              download
              data-testid="studio-elevation-download"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gold text-navy font-extrabold hover:bg-gold-dark hover:text-white"
            >
              <Download className="h-4 w-4" />
              Download 3D Elevation
            </a>
          </div>
        )}
      </div>

      {/* What next */}
      <div className="mt-5 rounded-3xl bg-navy-jaali text-white p-5 border-2 border-navy-600">
        <div className="text-[11px] uppercase tracking-[0.2em] text-gold font-bold">What next?</div>
        <ul className="mt-2 space-y-1.5 text-sm text-white/85">
          <li>• Share these files with your local contractor / architect for execution drawings.</li>
          <li>• Final structural, MEP, and authority approvals must be validated by a licensed professional.</li>
          <li>• Need help? WhatsApp us — we'll connect you with a verified Mitra in your district.</li>
        </ul>
      </div>
    </section>
  );
}
